import React, { useState, useContext, useEffect } from 'react'
import { CssEditorContext } from '../contexts/CssEditorContext'
import { setCss, getCss } from '../services/userServices'

export default function CssEditor({ onClose }) {
  
  const { cssVariables, updateCssVariable, resetCssVariables } = useContext(CssEditorContext)
  const [activeSection, setActiveSection] = useState('hero')
  const [showSuccess, setShowSuccess] = useState(false)
  const [position, setPosition] = useState({ x: window.innerWidth - 420, y: 20 })
  const [isDragging, setIsDragging] = useState(false)
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 })
  const [isMinimized, setIsMinimized] = useState(false)

  // CSS Variables for main page elements
  const [mainPageCss, setMainPageCss] = useState({
    hero: {
      titleColor: '#ffffff',
      subtitleColor: '#cccccc'
    },
    background: {
      color1: 'transparent',
      color2: 'transparent'
    },
    howTo: {
      titleColor: '#ffffff',
      iconColor: '#b71b1b',
      textColor: '#cccccc',
      badgeColor: '#b71b1b'
    },
          countdown: {
        titleColor: '#ffffff'
      },
    form: {
        inputBackground: '#000000',
        inputBorderColor: '#333333',
        inputTextColor: '#ffffff',
        buttonBackground: '#b71b1b',
        buttonTextColor: '#ffffff',
        buttonHoverColor: '#a61919',
        buttonBorderColor: '#b71b1b',
        secondaryButtonBackground: '#2d5aa0',
                secondaryButtonTextColor: '#ffffff',
        secondaryButtonBorderColor: '#2d5aa0'
      },
    tickets: {
      overlayColor: 'rgba(0,0,0,0.75)',
      popupBg: '#0d0d0d',
      popupBorder: '#1f2937',
      headerBg: 'transparent',
      headerText: '#ffffff',
      tabActiveText: '#ffffff',
      tabInactiveText: '#9ca3af',
      tabBorder: '#b71b1b',
      cardBg: 'transparent',
      cardBorder: '#374151',
      cardTitle: '#ffffff',
      emptyText: '#9ca3af'
    }
    })

  useEffect(() => {
    // Load saved CSS from API when component mounts
    loadSavedCss()
  }, [])

  // Apply CSS when mainPageCss changes (including after loading)
  useEffect(() => {
    if (mainPageCss && Object.keys(mainPageCss).length > 0) {
      applyCssToPage()
      
      // Ek olarak doğrudan DOM'a da uygula
      setTimeout(() => {
        applyCssDirectlyToDOM(mainPageCss)
      }, 100)
    }
  }, [mainPageCss])

  const applyCssToPage = () => {
    
    if (!mainPageCss || !mainPageCss.hero) {
      console.error('mainPageCss or mainPageCss.hero is undefined!')
      return
    }
    
    try {
      // CSS variable'ları hem :root hem de body'ye uygula
      const root = document.documentElement
      const body = document.body
      
      // Hero section
      const heroTitleColor = mainPageCss.hero.titleColor
      const heroSubtitleColor = mainPageCss.hero.subtitleColor
      
      root.style.setProperty('--hero-title', heroTitleColor)
      root.style.setProperty('--hero-subtitle', heroSubtitleColor)
      body.style.setProperty('--hero-title', heroTitleColor)
      body.style.setProperty('--hero-subtitle', heroSubtitleColor)
      
      // Verify CSS variables were set
      const rootTitleColor = root.style.getPropertyValue('--hero-title')
      const rootSubtitleColor = root.style.getPropertyValue('--hero-subtitle')
      const bodyTitleColor = body.style.getPropertyValue('--hero-title')
      const bodySubtitleColor = body.style.getPropertyValue('--hero-subtitle')
    
    // Background colors
    const bgColor1 = mainPageCss.background.color1
    const bgColor2 = mainPageCss.background.color2
    
    // Create gradients from selected colors (or transparent)
    const pageGradient = bgColor1 === 'transparent' && bgColor2 === 'transparent' 
      ? 'transparent' 
      : `linear-gradient(135deg, ${bgColor1} 0%, ${bgColor2} 50%, ${bgColor1} 100%)`
    
    const heroGradient = bgColor1 === 'transparent' && bgColor2 === 'transparent'
      ? 'transparent'
      : `linear-gradient(135deg, ${bgColor2} 0%, ${bgColor1} 50%, ${bgColor2} 100%)`
    
    // Set CSS variables for background gradients
    root.style.setProperty('--page-bg', pageGradient)
    root.style.setProperty('--hero-bg', heroGradient)

    // If a full background image is applied, don't overwrite it
    const hasBgImage = typeof document !== 'undefined' && document.body && document.body.dataset && document.body.dataset.hasBgImage === 'true'

    // Also apply directly to elements for immediate effect (skip when bg image present)
    if (!hasBgImage) {
      document.body.style.background = pageGradient
    }
    
    // Update main container background (skip when bg image present)
    const mainContainer = document.getElementById('main-container')
    if (mainContainer && !hasBgImage) {
      mainContainer.style.background = pageGradient
    }
    
    // Update all sections that use page-bg CSS variable
    const sectionsToUpdate = [
      '.hero-section',
      '[style*="var(--page-bg"]',
      '[style*="background-color: var(--page-bg"]',
      '[style*="background: var(--page-bg"]'
    ]
    
    if (!hasBgImage) {
      sectionsToUpdate.forEach(selector => {
        const elements = document.querySelectorAll(selector)
        elements.forEach(element => {
          if (element.style.background || element.style.backgroundColor) {
            element.style.background = pageGradient
          }
        })
      })
    }
    
    // Update hero section specifically (skip when bg image present)
    const heroSection = document.querySelector('.hero-section')
    if (heroSection && !hasBgImage) {
      heroSection.style.background = heroGradient
    }
    
    // Form section
    root.style.setProperty('--form-input-bg', mainPageCss.form.inputBackground)
    root.style.setProperty('--form-input-border', mainPageCss.form.inputBorderColor)
    root.style.setProperty('--form-input-text', mainPageCss.form.inputTextColor)
    root.style.setProperty('--form-button-bg', mainPageCss.form.buttonBackground)
    root.style.setProperty('--form-button-text', mainPageCss.form.buttonTextColor)
    root.style.setProperty('--form-button-hover', mainPageCss.form.buttonHoverColor)
    root.style.setProperty('--form-button-border', mainPageCss.form.buttonBorderColor)
    root.style.setProperty('--form-secondary-button-bg', mainPageCss.form.secondaryButtonBackground)
    root.style.setProperty('--form-secondary-button-text', mainPageCss.form.secondaryButtonTextColor)
    root.style.setProperty('--form-secondary-button-border', mainPageCss.form.secondaryButtonBorderColor)
    
    // How To section
    root.style.setProperty('--how-to-title', mainPageCss.howTo.titleColor)
    root.style.setProperty('--how-to-icon', mainPageCss.howTo.iconColor)
    root.style.setProperty('--how-to-text', mainPageCss.howTo.textColor)
    root.style.setProperty('--how-to-badge', mainPageCss.howTo.badgeColor)
    
    // Countdown Timer section
    root.style.setProperty('--countdown-title', mainPageCss.countdown.titleColor)

    // Tickets popup section
    root.style.setProperty('--tickets-overlay', mainPageCss.tickets.overlayColor)
    root.style.setProperty('--tickets-popup-bg', mainPageCss.tickets.popupBg)
    root.style.setProperty('--tickets-popup-border', mainPageCss.tickets.popupBorder)
    root.style.setProperty('--tickets-header-bg', mainPageCss.tickets.headerBg)
    root.style.setProperty('--tickets-header-text', mainPageCss.tickets.headerText)
    root.style.setProperty('--tickets-tab-active', mainPageCss.tickets.tabActiveText)
    root.style.setProperty('--tickets-tab-inactive', mainPageCss.tickets.tabInactiveText)
    root.style.setProperty('--tickets-tab-border', mainPageCss.tickets.tabBorder)
    root.style.setProperty('--tickets-card-bg', mainPageCss.tickets.cardBg)
    root.style.setProperty('--tickets-card-border', mainPageCss.tickets.cardBorder)
    root.style.setProperty('--tickets-card-title', mainPageCss.tickets.cardTitle)
    root.style.setProperty('--tickets-empty-text', mainPageCss.tickets.emptyText)
    
    // CSS variable'ları body'ye de uygula
    body.style.setProperty('--form-input-bg', mainPageCss.form.inputBackground)
    body.style.setProperty('--form-input-border', mainPageCss.form.inputBorderColor)
    body.style.setProperty('--form-input-text', mainPageCss.form.inputTextColor)
    body.style.setProperty('--form-button-bg', mainPageCss.form.buttonBackground)
    body.style.setProperty('--form-button-text', mainPageCss.form.buttonTextColor)
    body.style.setProperty('--form-button-hover', mainPageCss.form.buttonHoverColor)
    body.style.setProperty('--form-button-border', mainPageCss.form.buttonBorderColor)
    body.style.setProperty('--form-secondary-button-bg', mainPageCss.form.secondaryButtonBackground)
    body.style.setProperty('--form-secondary-button-text', mainPageCss.form.secondaryButtonTextColor)
    body.style.setProperty('--form-secondary-button-border', mainPageCss.form.secondaryButtonBorderColor)
    body.style.setProperty('--how-to-title', mainPageCss.howTo.titleColor)
    body.style.setProperty('--how-to-icon', mainPageCss.howTo.iconColor)
    body.style.setProperty('--how-to-text', mainPageCss.howTo.textColor)
    body.style.setProperty('--how-to-badge', mainPageCss.howTo.badgeColor)
    body.style.setProperty('--countdown-title', mainPageCss.countdown.titleColor)
    
    
    } catch (error) {
      console.error('Error applying CSS to page:', error)
    }
  }

  const applyCssDirectlyToDOM = (cssData) => {
    
    if (!cssData || !cssData.hero) {
      console.error('cssData or cssData.hero is undefined!')
      return
    }
    
    try {
      // Hero section - doğrudan elementlere uygula
      // Hero title - tüm h1 elementleri
      const heroTitles = document.querySelectorAll('h1')
      
      heroTitles.forEach((title, index) => {
        const titleText = title.textContent || title.innerText
        const titleColor = cssData.hero.titleColor
        title.style.color = titleColor
      })
      
      // Hero subtitle - tüm p elementleri (ilk birkaç tanesi)
      const heroSubtitles = document.querySelectorAll('p')
      
      heroSubtitles.slice(0, 3).forEach((subtitle, index) => {
        const subtitleText = subtitle.textContent || subtitle.innerText
        const subtitleColor = cssData.hero.subtitleColor
        subtitle.style.color = subtitleColor
      })
      
      // Background - doğrudan elementlere uygula
      if (cssData.background.color1 !== 'transparent' || cssData.background.color2 !== 'transparent') {
        const pageGradient = `linear-gradient(135deg, ${cssData.background.color1} 0%, ${cssData.background.color2} 50%, ${cssData.background.color1} 100%)`
        
        // Main container
        const mainContainer = document.getElementById('main-container')
        if (mainContainer) {
          mainContainer.style.background = pageGradient
          
        }
        
        // Hero section
        const heroSection = document.querySelector('.hero-section')
        if (heroSection) {
          heroSection.style.background = pageGradient
          
        }
        
        // Body
        document.body.style.background = pageGradient
        
      }
      
      // Form elements - doğrudan uygula
      const formInputs = document.querySelectorAll('input[type="email"], input[type="text"]')
      formInputs.forEach(input => {
        input.style.backgroundColor = cssData.form.inputBackground
        input.style.color = cssData.form.inputTextColor
        input.style.borderColor = cssData.form.inputBorderColor
        input.style.borderWidth = '2px'
        input.style.borderStyle = 'solid'
      })
      
      // Buttons - doğrudan uygula
      // Tüm butonları bul ve stillendir
      const allButtons = document.querySelectorAll('button')
      allButtons.forEach((button, index) => {
        const buttonText = button.textContent || button.innerText
        
        if (buttonText.includes('Bilet Talep Et') || buttonText.includes('Talep Et')) {
          // Primary button
          button.style.backgroundColor = cssData.form.buttonBackground
          button.style.color = cssData.form.buttonTextColor
          button.style.borderColor = cssData.form.buttonBorderColor
          button.style.borderWidth = '2px'
          button.style.borderStyle = 'solid'
          
        } else if (buttonText.includes('Biletleri Gör') || buttonText.includes('Gör')) {
          // Secondary button
          button.style.backgroundColor = cssData.form.secondaryButtonBackground
          button.style.color = cssData.form.secondaryButtonTextColor
          button.style.borderColor = cssData.form.secondaryButtonBorderColor
          button.style.borderWidth = '2px'
          button.style.borderStyle = 'solid'
          
        }
      })
      
      // How To section - doğrudan uygula
      // How To title (h2)
      const howToTitle = document.querySelector('h2.text-2xl, h2.text-3xl')
      if (howToTitle) {
        howToTitle.style.color = cssData.howTo.titleColor
        
      }
      
      // How To icons (svg)
      const howToIcons = document.querySelectorAll('svg.w-6, svg.h-6')
      howToIcons.forEach(icon => {
        icon.style.color = cssData.howTo.iconColor
        icon.style.fill = cssData.howTo.iconColor
      })
      
      // How To text (dd elements)
      const howToTexts = document.querySelectorAll('dd.text-gray-600')
      howToTexts.forEach(text => {
        text.style.color = cssData.howTo.textColor
      })
      
      // How To badge (dt elements)
      const howToBadges = document.querySelectorAll('dt.font-semibold')
      howToBadges.forEach(badge => {
        badge.style.color = cssData.howTo.badgeColor
      })
      
      // Countdown section - doğrudan uygula
      const countdownTitle = document.querySelector('h2.text-3xl, h2.text-4xl')
      if (countdownTitle) {
        countdownTitle.style.color = cssData.countdown.titleColor
        
      }
      
    } catch (error) {
      console.error('Error applying CSS directly to DOM:', error)
    }
  }

  const loadSavedCss = async () => {
    try {
      const spinId = typeof window !== 'undefined' ? localStorage.getItem('spinWheelId') : null
      const response = await getCss(spinId)
      let savedCss = null
      if (response && typeof response === 'object') {
        if (response.status === true && response.data) {
          savedCss = response.data
        } else if (response.data && typeof response.data === 'object') {
          savedCss = response.data
        } else {
          savedCss = response
        }
      }
      if (savedCss && Object.keys(savedCss).length > 0) {
        // Convert flat CSS data to structured format
        const structuredCss = {
          hero: {
            titleColor: savedCss.titleColor || '#ffffff',
            subtitleColor: savedCss.subtitleColor || '#cccccc'
          },
          background: {
            color1: savedCss.color1 || 'transparent',
            color2: savedCss.color2 || 'transparent'
          },
          form: {
            inputBackground: savedCss.inputBackground || '#000000',
            inputBorderColor: savedCss.inputBorderColor || '#333333',
            inputTextColor: savedCss.inputTextColor || '#ffffff',
            buttonBackground: savedCss.buttonBackground || '#b71b1b',
            buttonTextColor: savedCss.buttonTextColor || '#ffffff',
            buttonHoverColor: savedCss.buttonHoverColor || '#a61919',
            buttonBorderColor: savedCss.buttonBorderColor || '#b71b1b',
            secondaryButtonBackground: savedCss.secondaryButtonBackground || '#2d5aa0',
            secondaryButtonTextColor: savedCss.secondaryButtonTextColor || '#ffffff',
            secondaryButtonBorderColor: savedCss.secondaryButtonBorderColor || '#2d5aa0'
          },
          howTo: {
            titleColor: savedCss.howToTitleColor || '#ffffff',
            iconColor: savedCss.howToIconColor || '#b71b1b',
            textColor: savedCss.howToTextColor || '#cccccc',
            badgeColor: savedCss.howToBadgeColor || '#b71b1b'
          },
          countdown: {
            titleColor: savedCss.countdownTitleColor || '#ffffff'
          },
          tickets: {
            overlayColor: savedCss.ticketsOverlayColor || 'rgba(0,0,0,0.75)',
            popupBg: savedCss.ticketsPopupBg || '#0d0d0d',
            popupBorder: savedCss.ticketsPopupBorderColor || '#1f2937',
            headerBg: savedCss.ticketsHeaderBg || 'transparent',
            headerText: savedCss.ticketsHeaderText || '#ffffff',
            tabActiveText: savedCss.ticketsTabActiveText || '#ffffff',
            tabInactiveText: savedCss.ticketsTabInactiveText || '#9ca3af',
            tabBorder: savedCss.ticketsTabBorderColor || '#b71b1b',
            cardBg: savedCss.ticketsCardBg || 'transparent',
            cardBorder: savedCss.ticketsCardBorderColor || '#374151',
            cardTitle: savedCss.ticketsCardTitleColor || '#ffffff',
            emptyText: savedCss.ticketsEmptyTextColor || '#9ca3af'
          }
        }
        
        setMainPageCss(structuredCss)
        
        // CSS'leri hemen uygula - state güncellendikten sonra
        setTimeout(() => {
          applyCssToPage()
          
          // Ek olarak doğrudan DOM'a da uygula
          applyCssDirectlyToDOM(structuredCss)
        }, 100)
        
        // Ek güvenlik için 500ms sonra tekrar uygula
        setTimeout(() => {
          applyCssToPage()
          applyCssDirectlyToDOM(structuredCss)
        }, 500)
        
        // Son güvenlik için 1 saniye sonra tekrar uygula
        setTimeout(() => {
          applyCssToPage()
          applyCssDirectlyToDOM(structuredCss)
        }, 1000)
      } else {
        
      }
    } catch (error) {
      console.error('Error details:', error)
      console.error('Error message:', error.message)
      console.error('Error stack:', error.stack)
    }
  }

  const handleCssChange = (section, property, value) => {
    setMainPageCss(prev => {
      const newState = {
        ...prev,
        [section]: {
          ...prev[section],
          [property]: value
        }
      }
      
      // CSS'leri hemen uygula
      setTimeout(() => {
        applyCssToPage()
        applyCssDirectlyToDOM(newState)
      }, 10)
      
      // Ek güvenlik için 100ms sonra tekrar uygula
      setTimeout(() => {
        applyCssToPage()
        applyCssDirectlyToDOM(newState)
      }, 100)
      
      return newState
    })
  }

  const handleSaveCss = async () => {
    try {
      // Tüm CSS'leri direkt API'ye gönder
      const cssData = {
        // Hero bölümü
        titleColor: mainPageCss.hero.titleColor,
        subtitleColor: mainPageCss.hero.subtitleColor,
        
        // Background bölümü
        color1: mainPageCss.background.color1,
        color2: mainPageCss.background.color2,
        
        // Form bölümü
        inputBackground: mainPageCss.form.inputBackground,
        inputBorderColor: mainPageCss.form.inputBorderColor,
        inputTextColor: mainPageCss.form.inputTextColor,
        buttonBackground: mainPageCss.form.buttonBackground,
        buttonTextColor: mainPageCss.form.buttonTextColor,
        buttonHoverColor: mainPageCss.form.buttonHoverColor,
        buttonBorderColor: mainPageCss.form.buttonBorderColor,
        secondaryButtonBackground: mainPageCss.form.secondaryButtonBackground,
        secondaryButtonTextColor: mainPageCss.form.secondaryButtonTextColor,
        secondaryButtonBorderColor: mainPageCss.form.secondaryButtonBorderColor,
        
        // How To bölümü
        howToTitleColor: mainPageCss.howTo.titleColor,
        howToIconColor: mainPageCss.howTo.iconColor,
        howToTextColor: mainPageCss.howTo.textColor,
        howToBadgeColor: mainPageCss.howTo.badgeColor,
        
        // Countdown bölümü
        countdownTitleColor: mainPageCss.countdown.titleColor,

        // Tickets popup
        ticketsOverlayColor: mainPageCss.tickets.overlayColor,
        ticketsPopupBg: mainPageCss.tickets.popupBg,
        ticketsPopupBorderColor: mainPageCss.tickets.popupBorder,
        ticketsHeaderBg: mainPageCss.tickets.headerBg,
        ticketsHeaderText: mainPageCss.tickets.headerText,
        ticketsTabActiveText: mainPageCss.tickets.tabActiveText,
        ticketsTabInactiveText: mainPageCss.tickets.tabInactiveText,
        ticketsTabBorderColor: mainPageCss.tickets.tabBorder,
        ticketsCardBg: mainPageCss.tickets.cardBg,
        ticketsCardBorderColor: mainPageCss.tickets.cardBorder,
        ticketsCardTitleColor: mainPageCss.tickets.cardTitle,
        ticketsEmptyTextColor: mainPageCss.tickets.emptyText
      }
    
      const response = await setCss(cssData);
      if (response.success) {
        setShowSuccess(true)
        setTimeout(() => setShowSuccess(false), 3000)
      }
    } catch (error) {
      console.error('Error saving CSS:', error)
    }
  }

  const handleResetCss = () => {
    setMainPageCss({
      hero: {
        titleColor: '#ffffff',
        subtitleColor: '#cccccc'
      },
      background: {
        color1: 'transparent',
        color2: 'transparent'
      },
      form: {
        inputBackground: '#000000',
        inputBorderColor: '#333333',
        inputTextColor: '#ffffff',
        buttonBackground: '#b71b1b',
        buttonTextColor: '#ffffff',
        buttonHoverColor: '#a61919',
        buttonBorderColor: '#b71b1b',
        secondaryButtonBackground: '#2d5aa0',
        secondaryButtonTextColor: '#ffffff',
        secondaryButtonBorderColor: '#2d5aa0'
      },
      howTo: {
        titleColor: '#ffffff',
        iconColor: '#b71b1b',
        textColor: '#cccccc',
        badgeColor: '#b71b1b'
      },
      countdown: {
        titleColor: '#ffffff'
      }
    })
  }

  const handleMouseDown = (e) => {
    if (e.target.closest('.css-editor-header')) {
      setIsDragging(true)
      const rect = e.currentTarget.getBoundingClientRect()
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      })
    }
  }

  const handleMouseMove = (e) => {
    if (isDragging) {
      const newX = e.clientX - dragOffset.x
      const newY = e.clientY - dragOffset.y
      
      // Ekran sınırları içinde tut
      const maxX = window.innerWidth - 400
      const maxY = window.innerHeight - 600
      
      setPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY))
      })
    }
  }

  const handleMouseUp = () => {
    setIsDragging(false)
  }

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)

    return () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
      }
    }
  }, [isDragging, dragOffset])

  const renderHeroSection = () => (
    <div className="css-section">
      <h3>Hero Bölümü</h3>
      <div className="css-control-group">
        <div className="css-control-row">
          <label>Başlık Rengi:</label>
          <input
            type="color"
            value={mainPageCss.hero.titleColor}
            onChange={(e) => handleCssChange('hero', 'titleColor', e.target.value)}
          />
        </div>
                <div className="css-control-row">
          <label>Alt Başlık Rengi:</label>
          <input
            type="color"
            value={mainPageCss.hero.subtitleColor}
            onChange={(e) => handleCssChange('hero', 'subtitleColor', e.target.value)}
          />
        </div>
        <div className="css-control-row">
          <label>Arka Plan Renk 1:</label>
          <input
            type="color"
            value={mainPageCss.background.color1}
            onChange={(e) => handleCssChange('background', 'color1', e.target.value)}
          />
        </div>
        <div className="css-control-row">
          <label>Arka Plan Renk 2:</label>
          <input
            type="color"
            value={mainPageCss.background.color2}
            onChange={(e) => handleCssChange('background', 'color2', e.target.value)}
          />
        </div>
      </div>
    </div>
  )



  const renderFormSection = () => (
    <div className="css-section">
      <h3>Form Bölümü</h3>
      <div className="css-control-group">
        <div className="css-control-row">
          <label>Input Arka Plan:</label>
          <input
            type="color"
            value={mainPageCss.form.inputBackground}
            onChange={(e) => handleCssChange('form', 'inputBackground', e.target.value)}
          />
        </div>
        <div className="css-control-row">
          <label>Input Kenarlık:</label>
          <input
            type="color"
            value={mainPageCss.form.inputBorderColor}
            onChange={(e) => handleCssChange('form', 'inputBorderColor', e.target.value)}
          />
        </div>
        <div className="css-control-row">
          <label>Input Yazı Rengi:</label>
          <input
            type="color"
            value={mainPageCss.form.inputTextColor}
            onChange={(e) => handleCssChange('form', 'inputTextColor', e.target.value)}
          />
        </div>
        <div className="css-control-row">
          <label>Buton Arka Plan:</label>
          <input
            type="color"
            value={mainPageCss.form.buttonBackground}
            onChange={(e) => handleCssChange('form', 'buttonBackground', e.target.value)}
          />
        </div>
        <div className="css-control-row">
          <label>Buton Yazı Rengi:</label>
          <input
            type="color"
            value={mainPageCss.form.buttonTextColor}
            onChange={(e) => handleCssChange('form', 'buttonTextColor', e.target.value)}
          />
        </div>
        <div className="css-control-row">
          <label>Buton Hover:</label>
          <input
            type="color"
            value={mainPageCss.form.buttonHoverColor}
            onChange={(e) => handleCssChange('form', 'buttonHoverColor', e.target.value)}
          />
        </div>
        <div className="css-control-row">
          <label>Buton Kenarlık:</label>
          <input
            type="color"
            value={mainPageCss.form.buttonBorderColor}
            onChange={(e) => handleCssChange('form', 'buttonBorderColor', e.target.value)}
          />
        </div>
        <div className="css-control-row">
          <label>İkincil Buton BG:</label>
          <input
            type="color"
            value={mainPageCss.form.secondaryButtonBackground}
            onChange={(e) => handleCssChange('form', 'secondaryButtonBackground', e.target.value)}
          />
        </div>
        <div className="css-control-row">
          <label>İkincil Buton Yazı:</label>
          <input
            type="color"
            value={mainPageCss.form.secondaryButtonTextColor}
            onChange={(e) => handleCssChange('form', 'secondaryButtonTextColor', e.target.value)}
          />
        </div>
        <div className="css-control-row">
          <label>İkincil Buton Kenarlık:</label>
          <input
            type="color"
            value={mainPageCss.form.secondaryButtonBorderColor}
            onChange={(e) => handleCssChange('form', 'secondaryButtonBorderColor', e.target.value)}
          />
        </div>
      </div>
    </div>
  )

  const renderHowToSection = () => (
    <div className="css-section">
      <h3>📚 Nasıl Katılırım Bölümü</h3>
      <div className="css-control-group">
        <div className="css-control-row">
          <label>Başlık Rengi:</label>
          <input
            type="color"
            value={mainPageCss.howTo.titleColor}
            onChange={(e) => handleCssChange('howTo', 'titleColor', e.target.value)}
          />
        </div>
        <div className="css-control-row">
          <label>Icon Rengi:</label>
          <input
            type="color"
            value={mainPageCss.howTo.iconColor}
            onChange={(e) => handleCssChange('howTo', 'iconColor', e.target.value)}
          />
        </div>
        <div className="css-control-row">
          <label>Yazı Rengi:</label>
          <input
            type="color"
            value={mainPageCss.howTo.textColor}
            onChange={(e) => handleCssChange('howTo', 'textColor', e.target.value)}
          />
        </div>
        <div className="css-control-row">
          <label>Badge Rengi:</label>
          <input
            type="color"
            value={mainPageCss.howTo.badgeColor}
            onChange={(e) => handleCssChange('howTo', 'badgeColor', e.target.value)}
          />
        </div>
      </div>
    </div>
  )

  const renderCountdownSection = () => (
    <div className="css-section">
      <h3>⏰ Geri Sayım Sayacı</h3>
      <div className="css-control-group">
        <div className="css-control-row">
          <label>Başlık Rengi:</label>
          <input
            type="color"
            value={mainPageCss.countdown.titleColor}
            onChange={(e) => handleCssChange('countdown', 'titleColor', e.target.value)}
          />
        </div>
      </div>
    </div>
  )

  const renderActiveSection = () => {
    switch (activeSection) {
      case 'hero':
        return renderHeroSection()
      case 'form':
        return renderFormSection()
      case 'howTo':
        return renderHowToSection()
      case 'countdown':
        return renderCountdownSection()
      case 'tickets':
        return (
          <div className="css-section">
            <h3>🎟️ Biletlerim Popup</h3>
            <div className="css-control-group">
              <div className="css-control-row">
                <label>Overlay Rengi:</label>
                <input type="color" value={mainPageCss.tickets.overlayColor} onChange={(e) => handleCssChange('tickets', 'overlayColor', e.target.value)} />
              </div>
              <div className="css-control-row">
                <label>Popup Arkaplan:</label>
                <input type="color" value={mainPageCss.tickets.popupBg} onChange={(e) => handleCssChange('tickets', 'popupBg', e.target.value)} />
              </div>
              <div className="css-control-row">
                <label>Popup Kenarlık:</label>
                <input type="color" value={mainPageCss.tickets.popupBorder} onChange={(e) => handleCssChange('tickets', 'popupBorder', e.target.value)} />
              </div>
              <div className="css-control-row">
                <label>Başlık Arkaplan:</label>
                <input type="color" value={mainPageCss.tickets.headerBg} onChange={(e) => handleCssChange('tickets', 'headerBg', e.target.value)} />
              </div>
              <div className="css-control-row">
                <label>Başlık Yazı:</label>
                <input type="color" value={mainPageCss.tickets.headerText} onChange={(e) => handleCssChange('tickets', 'headerText', e.target.value)} />
              </div>
              <div className="css-control-row">
                <label>Aktif Sekme Yazı:</label>
                <input type="color" value={mainPageCss.tickets.tabActiveText} onChange={(e) => handleCssChange('tickets', 'tabActiveText', e.target.value)} />
              </div>
              <div className="css-control-row">
                <label>Pasif Sekme Yazı:</label>
                <input type="color" value={mainPageCss.tickets.tabInactiveText} onChange={(e) => handleCssChange('tickets', 'tabInactiveText', e.target.value)} />
              </div>
              <div className="css-control-row">
                <label>Sekme Alt Çizgi:</label>
                <input type="color" value={mainPageCss.tickets.tabBorder} onChange={(e) => handleCssChange('tickets', 'tabBorder', e.target.value)} />
              </div>
              <div className="css-control-row">
                <label>Kart Arkaplan:</label>
                <input type="color" value={mainPageCss.tickets.cardBg} onChange={(e) => handleCssChange('tickets', 'cardBg', e.target.value)} />
              </div>
              <div className="css-control-row">
                <label>Kart Kenarlık:</label>
                <input type="color" value={mainPageCss.tickets.cardBorder} onChange={(e) => handleCssChange('tickets', 'cardBorder', e.target.value)} />
              </div>
              <div className="css-control-row">
                <label>Kart Başlık:</label>
                <input type="color" value={mainPageCss.tickets.cardTitle} onChange={(e) => handleCssChange('tickets', 'cardTitle', e.target.value)} />
              </div>
              <div className="css-control-row">
                <label>Boş Liste Yazı:</label>
                <input type="color" value={mainPageCss.tickets.emptyText} onChange={(e) => handleCssChange('tickets', 'emptyText', e.target.value)} />
              </div>
            </div>
          </div>
        )
      default:
        return renderHeroSection()
    }
  }

  return (
    <div 
      className="css-editor-container"
            style={{
        left: `${position.x}px`,
        top: `${position.y}px`
      }}
      onMouseDown={handleMouseDown}
    >
      <div className="css-editor-main" style={{
        height: isMinimized ? '60px' : '600px'
      }}>
        <div className="css-editor-header" onMouseDown={handleMouseDown}>
          <h1>🎨 CSS Editörü</h1>
          <div>
            <button 
              className="css-editor-btn css-editor-btn-secondary" 
              type="button"
              onClick={() => setIsMinimized(!isMinimized)}
              style={{ marginRight: '8px', padding: '4px 8px', fontSize: '10px' }}
            >
              {isMinimized ? '🔽' : '🔼'}
            </button>
            <button className="css-editor-close" type="button" onClick={onClose}>✕</button>
          </div>
            </div>
        
        {!isMinimized && (
          <>
            <div className="css-editor-content">
              <div className="css-editor-sidebar">
                <div className="css-section">
                  <h3>Bölümler</h3>
                  <div className="css-control-group">
                    <button
                      className={`css-editor-btn ${activeSection === 'hero' ? 'css-editor-btn-primary' : 'css-editor-btn-secondary'}`}
                      onClick={() => setActiveSection('hero')}
                    >
                      🏠 Hero
                    </button>
                    <button
                      className={`css-editor-btn ${activeSection === 'form' ? 'css-editor-btn-primary' : 'css-editor-btn-secondary'}`}
                      onClick={() => setActiveSection('form')}
                    >
                      📝 Form
                    </button>
                    <button
                      className={`css-editor-btn ${activeSection === 'howTo' ? 'css-editor-btn-primary' : 'css-editor-btn-secondary'}`}
                      onClick={() => setActiveSection('howTo')}
                    >
                      📚 Nasıl Katılırım
                    </button>
                    <button
                      className={`css-editor-btn ${activeSection === 'countdown' ? 'css-editor-btn-primary' : 'css-editor-btn-secondary'}`}
                      onClick={() => setActiveSection('countdown')}
                    >
                      ⏰ Geri Sayım
                    </button>
                    <button
                      className={`css-editor-btn ${activeSection === 'tickets' ? 'css-editor-btn-primary' : 'css-editor-btn-secondary'}`}
                      onClick={() => setActiveSection('tickets')}
                    >
                      🎟️ Biletlerim
                    </button>

                  </div>
                      </div>
                      </div>

              <div className="css-editor-main-area">
                {renderActiveSection()}
                

                      </div>
                      </div>

            <div className="css-editor-actions">
              <div>
                <button className="css-editor-btn css-editor-btn-danger" type="button" onClick={handleResetCss}>
                  🔄 Sıfırla
                </button>
                      </div>
              <div>
                <button className="css-editor-btn css-editor-btn-secondary" type="button" onClick={onClose}>
                  ❌ Kapat
                </button>
                <button className="css-editor-btn css-editor-btn-primary" type="button" onClick={handleSaveCss}>
                  💾 Kaydet
                </button>
              </div>
            </div>
          </>
        )}
          </div>
      
      {showSuccess && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          background: '#10b981',
          color: 'white',
          padding: '15px 25px',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          zIndex: 3000
        }}>
          ✅ CSS ayarları başarıyla kaydedildi!
        </div>
      )}
    </div>
  )
}
